import React, { FC, useEffect, useMemo, useState } from 'react'
import SelectProductImageModal from './SelectedProductImage'
import { useAppDispatch } from 'src/redux/storeHooks'
import { toast } from 'react-toastify'
import { getApiErrorMessage } from 'src/marketConnect/helpers'
import { deleteProductImageInEditModeAsync } from 'src/redux/actions/omsProductActions'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons'

type ImageType = {
  file: File
  fileName: string
  size: number
  type: string
  imageUrl: string // preview (blob url)
  url?: string     // server url after upload (optional)
  id?: number      // server id after upload (optional)
  isNewSelectedFromGallery?: boolean
}

type Props = {
  values: any
  setFormikState: any
  maxImages?: number
  productId?: number
  mode?: 'create' | 'edit' | string
}

const MediaPicker: FC<Props> = ({ values, setFormikState, maxImages = 50, productId, mode }) => {
  const dispatch = useAppDispatch();

  const committed: ImageType[] = values.selectedImages || []
  const [staged, setStaged] = useState<ImageType[]>([])
  const [activeSrc, setActiveSrc] = useState<string>('')
  const [showGalleryModal, setShowGalleryModal] = useState(false)
  const allImages = useMemo(() => [...committed, ...staged], [committed, staged])

  const remainingSlots = maxImages - committed.length - staged.length
  const canAddMore = remainingSlots > 0

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleteType, setDeleteType] = useState<"committed" | "staged" | null>(null);

  // auto set active image and keep it valid
  useEffect(() => {
    if (!allImages.length) {
      if (activeSrc) setActiveSrc('')
      return
    }

    if (!activeSrc) {
      setActiveSrc(allImages[0].imageUrl)
      return
    }

    // if active was deleted, fallback to first
    if (!allImages.some((x) => x.imageUrl === activeSrc)) {
      setActiveSrc(allImages[0].imageUrl)
    }
  }, [allImages, activeSrc])

  const makePrimary = (imageUrl: string) => {
    setFormikState((prev: any) => {
      const current: ImageType[] = prev.values.selectedImages || []
      const idx = current.findIndex((img) => img.imageUrl === imageUrl || img.url === imageUrl)

      if (idx <= 0) {
        return prev
      }

      const next = [...current]
      const [picked] = next.splice(idx, 1)
      next.unshift(picked)

      return {
        ...prev,
        values: {
          ...prev.values,
          selectedImages: next,
        },
      }
    })
  }

  // cleanup blob urls on unmount
  useEffect(() => {
    return () => {
      ;[...committed, ...staged].forEach((img) => {
        if (img.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(img.imageUrl)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const revokeIfBlob = (img?: ImageType) => {
    if (img?.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(img.imageUrl)
  }

  const handleDeleteConfirmed = async () => {
    if (deleteIndex === null) return;

    try {
      if (deleteType === "staged") {
        deleteStaged(deleteIndex);
      } else {
        const img = values.selectedImages?.[deleteIndex];
        const imageId = img?.id;
        const resolvedProductId = productId;
        const isNew = img?.isNewSelectedFromGallery;
        if (mode === "edit" && imageId && resolvedProductId && !isNew) {
          const res: any = await dispatch(
            deleteProductImageInEditModeAsync({
              productId: resolvedProductId,
              imageId: Number(imageId),
            })
          ).unwrap();

          const status = Number(res?.status);
          if (status === 200 || status === 201) {
            toast.success(res?.message || "Image deleted successfully");
            deleteCommitted(deleteIndex);
          } else {
            toast.error(res?.errors?.detail || res?.message || "Failed to delete image");
            return;
          }
        } else {
          // create mode OR no id OR newly selected -> just remove locally
          deleteCommitted(deleteIndex);
        }
      }
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, "Failed to delete image"));
    } finally {
      setShowDeleteModal(false);
      setDeleteIndex(null);
      setDeleteType(null);
    }
  };

  const deleteCommitted = (index: number) => {
    setFormikState((prev: any) => {
      const current: ImageType[] = prev.values.selectedImages || []
      const removed = current[index]
      revokeIfBlob(removed)

      return {
        ...prev,
        values: {
          ...prev.values,
          selectedImages: current.filter((_: any, i: number) => i !== index),
        },
      }
    })
  }

  const deleteStaged = (index: number) => {
    setStaged((prev) => {
      const copy = [...prev]
      const removed = copy[index]
      copy.splice(index, 1)
      revokeIfBlob(removed)
      return copy
    })
  }

  const activeImage = allImages.find((x) => x.imageUrl === activeSrc) || allImages[0]

  const removeActiveImage = () => {
    if (!activeImage) return

    const committedIndex = committed.findIndex((x) => x.imageUrl === activeImage.imageUrl)
    if (committedIndex !== -1) {
      setDeleteIndex(committedIndex)
      setDeleteType("committed")
      setShowDeleteModal(true)
      return
    }
    const stagedIndex = staged.findIndex((x) => x.imageUrl === activeImage.imageUrl)
    if (stagedIndex !== -1) {
      setDeleteIndex(stagedIndex)
      setDeleteType("staged")
      setShowDeleteModal(true)
    }
  }

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allImages.length <= 1) return;
    const currentIdx = allImages.findIndex(x => x.imageUrl === activeSrc);
    const prevIdx = currentIdx <= 0 ? allImages.length - 1 : currentIdx - 1;
    setActiveSrc(allImages[prevIdx].imageUrl);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allImages.length <= 1) return;
    const currentIdx = allImages.findIndex(x => x.imageUrl === activeSrc);
    const nextIdx = currentIdx >= allImages.length - 1 ? 0 : currentIdx + 1;
    setActiveSrc(allImages[nextIdx].imageUrl);
  };

  const isPrimaryActive = allImages.length > 0 && allImages[0].imageUrl === activeImage?.imageUrl;

  const mediaPickerStyles = `
    @keyframes mp-gradient-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes mp-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes mp-pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
      70% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
      100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
    }
    @keyframes mp-float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-6px); }
    }
    @keyframes mp-glow-pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 0.8; }
    }
    .mp-scroll-area::-webkit-scrollbar { width: 4px; }
    .mp-scroll-area::-webkit-scrollbar-track { background: transparent; }
    .mp-scroll-area::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.3); border-radius: 100px; }
    .mp-scroll-area::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.5); }
    .mp-thumb-card { position: relative; }
    .mp-thumb-card .mp-delete-btn { opacity: 0; transform: scale(0.8) translateY(4px); }
    .mp-thumb-card:hover .mp-delete-btn { opacity: 1; transform: scale(1) translateY(0); }
  `;

  return (
    <>
    <div style={{
      width: '100%',
      position: 'relative',
      borderRadius: 28,
      padding: 2,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #667eea 100%)',
      backgroundSize: '300% 300%',
      boxSizing: 'border-box',
    }}>
      <style>{mediaPickerStyles}</style>

      <div style={{
        width: '100%',
        height: '34rem',
        background: '#ffffff',
        borderRadius: 26,
        padding: 20,
        boxSizing: 'border-box',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.12)',
      }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', height: '100%' }}>

          {/* LEFT — CINEMATIC PREVIEW */}
          <div style={{ flex: '0 0 70%', minWidth: 300, position: 'relative' }}>
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: 22,
                background: activeImage ? 'linear-gradient(145deg, #0c1222 0%, #1a1a2e 50%, #16213e 100%)' : '#f8fafc',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: activeImage ? 'inset 0 2px 20px rgba(0,0,0,0.3), 0 8px 32px -8px rgba(0,0,0,0.3)' : 'inset 0 0 0 1px #e2e8f0',
              }}
            >
              {activeImage ? (
                <>
                  {/* Animated Glowing Background — color-extracted aura */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: -80,
                      backgroundImage: `url(${activeImage.imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'blur(80px) saturate(1.8)',
                      opacity: 0.55,
                      transform: 'scale(1.3)',
                      animation: 'mp-glow-pulse 4s ease-in-out infinite',
                      transition: 'background-image 0.6s ease',
                    }}
                  />

                  {/* Noise / grain overlay for cinematic texture */}
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', opacity: 0.03,
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                    backgroundSize: '128px 128px',
                  }} />

                  {/* Vignette overlay */}
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)',
                  }} />

                  {/* Top fade for badges */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 100,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
                    zIndex: 4, pointerEvents: 'none',
                  }} />

                  {/* Bottom fade */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.4) 0%, transparent 100%)',
                    zIndex: 4, pointerEvents: 'none',
                  }} />

                  {/* The actual product image */}
                  <img
                    src={activeImage.imageUrl}
                    alt={activeImage.fileName}
                    style={{
                      maxWidth: '88%',
                      maxHeight: '88%',
                      objectFit: 'contain',
                      zIndex: 5,
                      filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.5))',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderRadius: 8,
                    }}
                  />

                  {/* PRIMARY badge / SET PRIMARY button */}
                  {isPrimaryActive ? (
                    <div style={{
                      position: 'absolute', top: 20, left: 20, zIndex: 15,
                      background: 'rgba(99, 102, 241, 0.85)',
                      color: '#ffffff', fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      padding: '5px 7px', borderRadius: 6,
                      display: 'flex', alignItems: 'center', gap: 5,
                      border: '1px solid rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      <span style={{ fontSize: 11 }}>★</span> PRIMARY
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        makePrimary(activeImage.imageUrl);
                      }}
                      style={{
                        position: 'absolute', top: 20, left: 20, zIndex: 15,
                        background: 'rgba(15, 23, 42, 0.65)',
                        color: '#f8fafc', fontSize: 10, fontWeight: 700,
                        letterSpacing: '0.5px', textTransform: 'uppercase',
                        padding: '5px 7px', borderRadius: 6,
                        display: 'flex', alignItems: 'center', gap: 5,
                        border: '1px solid rgba(255,255,255,0.15)',
                        cursor: 'pointer',
                        backdropFilter: 'blur(8px)',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.85)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.65)';
                      }}
                    >
                      <span style={{ fontSize: 11 }}>☆</span> SET PRIMARY
                    </button>
                  )}

                  {/* Nav: Left Arrow */}
                  {allImages.length > 1 && (
                    <button
                      type="button"
                      onClick={handlePrev}
                      style={{
                        position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 'bold',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(16px)',
                        zIndex: 15,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                        e.currentTarget.style.color = '#0f172a';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.12)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      ‹
                    </button>
                  )}

                  {/* Nav: Right Arrow */}
                  {allImages.length > 1 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      style={{
                        position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 'bold',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(16px)',
                        zIndex: 15,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                        e.currentTarget.style.color = '#0f172a';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.12)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      ›
                    </button>
                  )}

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={removeActiveImage}
                    style={{
                      position: 'absolute', top: 20, right: 20,
                      width: 40, height: 40, borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(239, 68, 68, 0.7)',
                      color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(16px)',
                      zIndex: 15,
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#ef4444';
                      e.currentTarget.style.transform = 'scale(1.15) rotate(8deg)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(239,68,68,0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.7)';
                      e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    aria-label="Remove image"
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>

                  {/* Slide counter pill */}
                  <div style={{
                    position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(0,0,0,0.45)',
                    padding: '8px 18px', borderRadius: 100,
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    zIndex: 15,
                  }}>
                    {allImages.map((img, i) => (
                      <div
                        key={img.imageUrl}
                        onClick={(e) => { e.stopPropagation(); setActiveSrc(img.imageUrl); }}
                        style={{
                          width: img.imageUrl === activeSrc ? 24 : 8,
                          height: 8,
                          borderRadius: 100,
                          background: img.imageUrl === activeSrc
                            ? 'linear-gradient(90deg, #818cf8, #c084fc)'
                            : 'rgba(255,255,255,0.35)',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    ))}
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, marginLeft: 6 }}>
                      {allImages.findIndex((x) => x.imageUrl === activeSrc) + 1}/{allImages.length}
                    </span>
                  </div>
                </>
              ) : (
                <div style={{
                  color: '#475569', fontSize: 15, fontWeight: 500,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, zIndex: 10,
                }}>
                  <div style={{
                    animation: 'mp-float 3s ease-in-out infinite',
                    background: 'rgba(0,0,0,0.02)', padding: 28, borderRadius: 24,
                    border: '1px dashed #cbd5e1',
                  }}>
                    <svg width="52" height="52" fill="none" stroke="#64748b" viewBox="0 0 24 24" style={{ opacity: 0.8 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span style={{ color: '#64748b', letterSpacing: '0.3px', fontWeight: 600 }}>Drop or select an image to begin</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — GALLERY DOCK */}
          <div style={{
            flex: '0 0 30%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            background: '#f8fafc', borderRadius: 22,
            border: '1px solid #e2e8f0',
          }}>
            {/* Gallery Header */}
            <div style={{
              padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.2px' }}>Gallery</span>
              <span style={{
                fontSize: 12, fontWeight: 700, color: '#6366f1',
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                padding: '4px 12px', borderRadius: 100,
                border: '1px solid #c7d2fe',
              }}>
                {allImages.length} / {maxImages}
              </span>
            </div>

            {/* Scrollable Thumbnails */}
            <div
              className="mp-scroll-area"
              style={{ padding: 16, overflowY: 'auto', flex: 1, scrollbarWidth: 'thin' }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>

                {/* Upload Tile */}
                {canAddMore && (
                  <button
                    type="button"
                    onClick={() => setShowGalleryModal(true)}
                    style={{
                      aspectRatio: '1/1', borderRadius: 16,
                      background: '#fff',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#6366f1',
                      gap: 10, border: '2px dashed #c7d2fe',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = '#6366f1';
                      e.currentTarget.style.background = '#eef2ff';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px -8px rgba(99,102,241,0.25)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '#c7d2fe';
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                      padding: 10, borderRadius: '50%',
                    }}>
                      <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    Upload
                  </button>
                )}

                {/* Thumbnail Cards */}
                {allImages.map((img, idx) => {
                  const committedIndex = committed.findIndex((c) => c.imageUrl === img.imageUrl)
                  const isCommitted = committedIndex !== -1
                  const stagedIndex = staged.findIndex((s) => s.imageUrl === img.imageUrl)
                  const isStaged = stagedIndex !== -1
                  const position = idx + 1
                  const isActive = img.imageUrl === activeSrc

                  return (
                    <div
                      key={img.imageUrl}
                      className="mp-thumb-card"
                      onClick={() => {
                        setActiveSrc(img.imageUrl)
                      }}
                      style={{
                        aspectRatio: '1/1', borderRadius: 16, overflow: 'hidden',
                        position: 'relative', cursor: 'pointer', background: '#fff',
                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isActive
                          ? '0 0 0 3px #fff, 0 0 0 5px #6366f1, 0 12px 28px -6px rgba(99,102,241,0.35)'
                          : '0 2px 8px -2px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.04)',
                        transform: isActive ? 'scale(0.95)' : 'scale(1)',
                        ...(isActive ? { animation: 'mp-pulse-ring 2s ease-in-out infinite' } : {}),
                      }}
                      onMouseOver={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 16px 32px -8px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(0,0,0,0.06)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 8px -2px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.04)';
                        }
                      }}
                    >
                      <img
                        src={img.imageUrl}
                        alt={img.fileName}
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: isActive ? 'scale(1.12)' : 'scale(1)',
                        }}
                      />

                      {/* Hover darkening overlay */}
                      <div style={{
                        position: 'absolute', inset: 0, zIndex: 1,
                        background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.35) 100%)',
                        pointerEvents: 'none',
                      }} />

                      {/* Position badge */}
                      <div style={{
                        position: 'absolute', left: 8, top: 8,
                        background: isActive
                          ? 'rgba(99, 102, 241, 0.9)'
                          : 'rgba(255,255,255,0.85)',
                        color: isActive ? '#fff' : '#1e293b',
                        fontSize: 11, fontWeight: 700,
                        width: 22, height: 22, borderRadius: 6,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 3, backdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      }}>
                        {position}
                      </div>

                      {/* Star badge for primary */}
                      {position === 1 && (
                        <div style={{
                          position: 'absolute', right: 8, top: 8,
                          background: 'rgba(99, 102, 241, 0.9)',
                          color: '#fff', width: 22, height: 22, borderRadius: 6,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, zIndex: 3,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          backdropFilter: 'blur(4px)',
                        }}>
                          ★
                        </div>
                      )}

                      {/* Delete button — reveal on hover */}
                      <button
                        type="button"
                        className="mp-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isCommitted) {
                            setDeleteIndex(committedIndex)
                            setDeleteType("committed")
                            setShowDeleteModal(true)
                          } else if (isStaged) {
                            setDeleteIndex(stagedIndex)
                            setDeleteType("staged")
                            setShowDeleteModal(true)
                          }
                        }}
                        style={{
                          position: 'absolute', bottom: 8, right: 8,
                          width: 28, height: 28, borderRadius: '50%',
                          border: 'none',
                          background: 'rgba(255,255,255,0.9)', color: '#ef4444',
                          cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, zIndex: 3,
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#ef4444';
                          e.currentTarget.style.color = '#fff';
                          e.currentTarget.style.transform = 'scale(1.15)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                          e.currentTarget.style.color = '#ef4444';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        aria-label="Remove thumbnail"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <SelectProductImageModal
        show={showGalleryModal}
        images={[]} // not used; modal fetches itself
        onClose={() => setShowGalleryModal(false)}
        initialSelected={committed}
        onSelect={(imgsFromModal) => {
          if (!imgsFromModal?.length) return

          const allowed = imgsFromModal.slice(0, remainingSlots)

          setFormikState((prev: any) => {
            const current = prev.values.selectedImages || []

            const existingKeys = new Set(
              current.map((x: any) => x.url || x.imageUrl) // server urls are stable
            )

            const mapped = allowed
              .map((img: any) => ({
                file: img.file,
                fileName: img.fileName,
                size: img.size || 0,
                type: img.type || "image/*",
                imageUrl: img.url || img.imageUrl,
                url: img.url,
                id: img.id,
                __source: img.__source,
                isNewSelectedFromGallery: true,
              }))
              .filter((x: any) => {
                const k = x.url || x.imageUrl
                if (!k) return false
                if (existingKeys.has(k)) return false
                existingKeys.add(k)
                return true
              })

            return {
              ...prev,
              values: {
                ...prev.values,
                selectedImages: [...mapped, ...current]
              },
            }
          })

          //  always set active to newest added
          if (allowed.length) {
            const last = allowed[allowed.length - 1]
            setActiveSrc(last.url || last.imageUrl)
          }
          setShowGalleryModal(false)
        }}
      />

      <CommonAlertModal
        title="Delete Image?"
        subtitle="This image will be removed from the product."
        isVisible={showDeleteModal}
        isTermsVisible={false}
        onSuccess={handleDeleteConfirmed}
        onCancel={() => {
          setDeleteIndex(null);
          setDeleteType(null);
          setShowDeleteModal(false);
        }}
        successBtnTitle="Delete"
        cancelBtnTitle="Cancel"
        successBtnClasses="btn btn-sm btn-danger m-2"
      />
    </>
  )
}

export default MediaPicker

